import { useState } from "react"
import { 
  Box, 
  Button, 
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid, 
  IconButton, 
  Stack, 
  Typography 
} from "@mui/material"
import { useFirestore, useFirebase } from "react-redux-firebase"
import { useSelector } from "react-redux"
import { authSelector } from "../../../auth/authSlice"
import { enqueueSnackbar } from "notistack"
import { Delete as DeleteIcon, FileUpload as FileUploadIcon } from "@mui/icons-material"
import { useFirestoreFetch } from "../../../../app/services/hooks/fetch/useFirestoreFetch"
import Iconify from "../../../../app/iconify"
import { InternalFile } from "../../../../app/interfaces/invoice"

interface CustomerFilesProps {
  customer: {
    id: string
    [key: string]: any
  }
}

export function CustomerFiles({ customer }: CustomerFilesProps) {
  const [isUploading, setIsUploading] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState<{open: boolean, file?: InternalFile}>({
    open: false
  })
  const firestore = useFirestore()
  const firebase = useFirebase()
  const { fsOrgPrefix, orgData, userData } = useSelector(authSelector)

  // Custom hook to fetch files
  const { data: customerFiles = [], fetching, refresh } = useFirestoreFetch(
    () => 
      firestore
        .collection("orgs")
        .doc(orgData?.id)
        .collection("customers")
        .doc(customer.id)
        .collection("files")
        .get()
        .then(snapshot => 
          snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          } as InternalFile))
        )
  )

  async function handleFileUpload(event: React.ChangeEvent<HTMLInputElement>) {
    if (!event.target.files?.length) return

    setIsUploading(true)
    const file = event.target.files[0]
    const filePath = `orgs/${orgData?.id}/customers/${customer.id}/files/${file.name}`

    try {
      // 1. Upload file to Firebase Storage
      const storageRef = firebase.storage().ref()
      const fileRef = storageRef.child(filePath)
      await fileRef.put(file)

      // 2. Get the download URL
      const downloadUrl = await fileRef.getDownloadURL()

      // 3. Create metadata document in Firestore
      await firestore
        .collection("orgs")
        .doc(orgData?.id)
        .collection("customers")
        .doc(customer.id)
        .collection("files")
        .add({
          name: file.name,
          downloadUrl,
          size: Math.round(file.size / 1024), // Convert to KB
          uploadedBy: userData.id,
          createdOn: new Date()
        })

      enqueueSnackbar("File uploaded successfully", { variant: "success" })
      refresh() // Refresh the list
    } catch (error) {
      console.error("Error uploading file:", error)
      enqueueSnackbar("Error uploading file", { variant: "error" })
    } finally {
      setIsUploading(false)
    }
  }

  async function handleDeleteFile(fileId: string) {
    try {
      // 1. Get the file document to get the storage path
      const fileDoc = await firestore
        .collection("orgs")
        .doc(orgData?.id)
        .collection("customers")
        .doc(customer.id)
        .collection("files")
        .doc(fileId)
        .get()

      const fileData = fileDoc.data() as InternalFile
      fileData.id = fileDoc.id

      // 2. Delete from Storage if path exists
      if (fileData?.downloadUrl) {
        const storageRef = firebase.storage().ref()
        const fileRef = storageRef.child(fileData.downloadUrl)
        await fileRef.delete().catch(err => console.warn('Storage file may not exist:', err))
      }

      // 3. Delete the document
      await fileDoc.ref.delete()

      enqueueSnackbar("File deleted successfully", { variant: "success" })
      refresh() // Refresh the list
    } catch (error) {
      console.error("Error deleting file:", error)
      enqueueSnackbar("Error deleting file", { variant: "error" })
    }
  }

  const handleConfirmDelete = (file: InternalFile) => {
    setDeleteConfirm({ open: true, file })
  }

  const handleCloseConfirm = () => {
    setDeleteConfirm({ open: false })
  }

  const handleConfirmedDelete = async () => {
    if (deleteConfirm.file?.id) {
      await handleDeleteFile(deleteConfirm.file.id)
    }
    handleCloseConfirm()
  }

  if (fetching) return <Typography>Loading files...</Typography>

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Customer Files</Typography>
        <Button
          variant="contained"
          component="label"
          startIcon={<FileUploadIcon />}
          disabled={isUploading}
        >
          Upload File
          <input
            type="file"
            hidden
            onChange={handleFileUpload}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
          />
        </Button>
      </Stack>

      <Grid container spacing={2}>
        {customerFiles.map((file: InternalFile) => (
          <Grid item xs={12} key={file.id}>
            <Box
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}
            >
              <Stack spacing={1}>
                <Typography>{file.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Uploaded {file.createdOn instanceof Date ? file.createdOn.toLocaleDateString() : new Date(file.createdOn).toLocaleDateString()}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1}>
                {file.downloadUrl && (
                  <IconButton
                    onClick={() => window.open(file.downloadUrl, '_blank')}
                    color="primary"
                    size="small"
                  >
                    <Iconify icon="mdi:file-download" />
                  </IconButton>
                )}
                <IconButton
                  onClick={() => handleConfirmDelete(file)}
                  color="error"
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Stack>
            </Box>
          </Grid>
        ))}

        {customerFiles.length === 0 && (
          <Grid item xs={12}>
            <Typography color="text.secondary" align="center">
              No files uploaded yet
            </Typography>
          </Grid>
        )}
      </Grid>

      <Dialog
        open={deleteConfirm.open}
        onClose={handleCloseConfirm}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Delete File?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete "{deleteConfirm.file?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirm}>Cancel</Button>
          <Button onClick={handleConfirmedDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
